import { createAsyncThunk, createEntityAdapter, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { chain, difference, map } from 'lodash';
import { availablePageSizes } from 'config';
import { createPaginationAdapter } from '@payer-admin-ui/components-v2/src/state/paginationAdapter';
import { createFilterAdapter } from '@payer-admin-ui/components-v2/src/state/filterAdapter';
import { createMultiColumnSortingAdapter } from '@payer-admin-ui/components-v2/src/state/sortingAdapter';
import { patch } from '@payer-admin-ui/components-v2/src/functions/lodashExtensions';
import { GetPaymentResponse } from '@payer-admin-ui/api/src/payments/responses/GetPaymentResponse';
import { PaymentStatus } from '@payer-admin-ui/api/src/payments/models/PaymentStatus';
import { CurrencyCode } from '@payer-admin-ui/api/src/invoices/models/CurrencyCode';
import { PaymentType } from '@payer-admin-ui/api/src/invoices/models/PaymentType';
import { LocalDate } from '@payer-admin-ui/api/src/external/java/LocalDate';
import {
  AppEntityState,
  FetchingEntityState,
  ListViewWithFilterAndMultiSortState,
  SingleSortType,
  SingleViewState,
} from '@payer-admin-ui/components-v2/src/types/state';
import { PaymentPageItem } from '@payer-admin-ui/api/src/payments/responses/PaymentPageItem';
import { SortDirection } from '@payer-admin-ui/components/src/Table/types';
import { paymentAPI } from '../../../redux/paymentAPI';
import { MerchantAdminState } from '../../../redux/store';
import { updateTableSettings, clearTableSettings } from '../../../redux/userSettings';

export const PAYMENTS_TABLE_SETTINGS_KEY = 'payments';

const updModificationTime = <T extends PaymentPageItem>(payment: T): T => {
  const sortedDetails = payment.paymentOperations.sort(
    (a, b) => new Date(b.createdDate).getTime() - new Date(a.createdDate).getTime(),
  );
  const lastUpdated = sortedDetails[0];
  payment.modificationTime = lastUpdated ? lastUpdated.createdDate : payment.modificationTime;
  return payment;
};

export const fetchPayment = createAsyncThunk<GetPaymentResponse, { id: number }>(
  'payments/fetchById',
  async ({ id }) => {
    const response = await paymentAPI.fetchById({ id });
    return updModificationTime(response);
  },
);

export const fetchPaymentsPage = createAsyncThunk<
  { totalCount: number; contents: GetPaymentResponse[] },
  void,
  { state: MerchantAdminState }
>('payments/fetchPage', async (_, { getState }) => {
  const { page, pageSize, appliedFilters, filterOverride, sortState } = getState().paymentsPage.listView;
  const sort = (sortState || []).filter(s => s.sortDirection).map(s => `${s.column}|${s.sortDirection.toUpperCase()}`);
  const effectiveFilters = filterOverride ?? appliedFilters;
  const response = await paymentAPI.fetchPaymentsPage({
    page,
    pageSize,
    filters: effectiveFilters,
    sort,
  });
  const paymentsWithLatestActualModificationTime = response.contents.map(payment => {
    return updModificationTime(payment);
  });
  return {
    contents: paymentsWithLatestActualModificationTime,
    totalCount: response.totalCount,
  };
});

export const applyPaymentsFilters = createAsyncThunk<void, void, { state: MerchantAdminState }>(
  'payments/applyPaymentsFilters',
  async (_, { getState, dispatch }) => {
    dispatch(paymentsPageSlice.actions.applyFilters());
    dispatch(
      updateTableSettings({
        component: PAYMENTS_TABLE_SETTINGS_KEY,
        update: { appliedFilters: getState().paymentsPage.listView.appliedFilters },
      }),
    );
  },
);

export const setPaymentsSort = createAsyncThunk<
  void,
  SingleSortType<PaymentsSortableColumn>,
  { state: MerchantAdminState }
>('payments/setPaymentsSort', async (sort, { getState, dispatch }) => {
  dispatch(paymentsPageSlice.actions.setSort(sort));
  const sortState = getState().paymentsPage.listView.sortState;
  dispatch(
    updateTableSettings({
      component: PAYMENTS_TABLE_SETTINGS_KEY,
      update: { sortOrders: sortState },
    }),
  );
  dispatch(fetchPaymentsPage());
});

export const syncUserSettings = createAsyncThunk<void, void, { state: MerchantAdminState }>(
  'payments/syncUserSettings',
  async (_, { getState, dispatch }) => {
    const settings = getState().userSettings?.tableSettings?.[PAYMENTS_TABLE_SETTINGS_KEY];
    const pageSize = settings?.pageSize;
    const sortModel = settings?.sortOrders;
    const appliedFilters = settings?.appliedFilters;
    if (pageSize) {
      dispatch(setPageSize(pageSize));
    }
    if (sortModel) {
      dispatch(setSorts(sortModel as SingleSortType<PaymentsSortableColumn>[]));
    }
    if (appliedFilters) {
      dispatch(
        setFilter({
          ...getState().paymentsPage.listView.filters,
          ...appliedFilters,
        }),
      );
      dispatch(paymentsPageSlice.actions.applyFilters());
    }
    dispatch(setInited());
  },
);

export const clearPaymentsTableSettings = createAsyncThunk<void, void, { state: MerchantAdminState }>(
  'payments/clearTableSettings',
  async (_, { dispatch }) => {
    dispatch(setFilter(initialFilters));
    dispatch(paymentsPageSlice.actions.applyFilters());
    dispatch(paymentsPageSlice.actions.resetSort());
    dispatch(setPageSize(availablePageSizes[0]));
    dispatch(
      clearTableSettings({
        component: PAYMENTS_TABLE_SETTINGS_KEY,
        customDefaultSettings: {
          appliedFilters: initialFilters,
        },
      }),
    );
  },
);

export type PaymentFilters = {
  statuses: PaymentStatus[];
  currencyCodes: CurrencyCode[];
  paymentMethods: PaymentType[];
  creationDateStart: LocalDate;
  creationDateEnd: LocalDate;
  amountStart: string | null;
  amountEnd: string | null;
  search: string;
};

export enum PaymentsSortableColumn {
  id = 'id',
  externalPaymentReference = 'externalPaymentReference',
  type = 'type',
  creationTime = 'creationTime',
  amount = 'amount',
}

export type PaymentsGlobalState = AppEntityState<GetPaymentResponse | PaymentPageItem, number> &
  SingleViewState<number> &
  ListViewWithFilterAndMultiSortState<number, PaymentFilters, PaymentsSortableColumn> & {
    listView: {
      inited: boolean;
      filterOverride: PaymentFilters | null;
    };
  };

export type PaymentEntity = FetchingEntityState<GetPaymentResponse | PaymentPageItem, number>;

const emptyFilters: PaymentFilters = {
  statuses: [],
  paymentMethods: [],
  currencyCodes: [],
  creationDateStart: '',
  creationDateEnd: '',
  amountStart: null,
  amountEnd: null,
  search: '',
};

const entityAdapter = createEntityAdapter<PaymentEntity>();
const paginationAdapter = createPaginationAdapter<PaymentsGlobalState>({
  selectState: state => state.listView,
});

const filterAdapter = createFilterAdapter<PaymentsGlobalState, PaymentFilters>({
  selectState: state => state.listView,
  emptyFilters,
  reset: paginationAdapter.reset,
});

const sortingAdapter = createMultiColumnSortingAdapter<PaymentsGlobalState, PaymentsSortableColumn>({
  selectState: state => state.listView,
  reset: paginationAdapter.reset,
});

const initialFilters: PaymentFilters = {
  ...emptyFilters,
};

const initialSort = {
  column: PaymentsSortableColumn.creationTime,
  sortDirection: SortDirection.desc,
};

const initialState: PaymentsGlobalState = entityAdapter.getInitialState({
  singleView: {
    cachedIds: [],
  },
  listView: {
    isFetching: false,
    error: null,
    inited: false,
    totalCount: 0,
    ids: [],
    page: 0, // zero-indexed in state, one-indexed in UI
    pageSize: availablePageSizes[0],
    filters: initialFilters,
    appliedFilters: initialFilters,
    sortState: [initialSort],
    filterOverride: null,
  },
});

const pruneCache = (state: PaymentsGlobalState) => {
  entityAdapter.removeMany(state, difference(state.ids, state.singleView.cachedIds, state.listView.ids));
};

const SINGLE_CACHE_SIZE = 10;

const upsertOnePruneCache = (state: PaymentsGlobalState, entity: PaymentEntity) => {
  entityAdapter.upsertOne(state, entity);
  state.singleView.cachedIds = chain([entity.id]).union(state.singleView.cachedIds).take(SINGLE_CACHE_SIZE).value();
  pruneCache(state);
};

const paymentsPageSlice = createSlice({
  name: 'paymentsPage',
  initialState,
  reducers: {
    resetError: state => {
      state.listView.error = null;
    },
    resetEntityError: (state, { payload: id }) => {
      entityAdapter.updateOne(state, { id, changes: { error: null } });
    },
    setInited: state => {
      state.listView.inited = true;
    },
    setFilterOverride: (state, { payload }: PayloadAction<PaymentFilters>) => {
      state.listView.filterOverride = payload;
      state.listView.page = 0;
    },
    clearFilterOverride: state => {
      state.listView.filterOverride = null;
    },
    setPage: paginationAdapter.setPage,
    setPageSize: paginationAdapter.setPageSize,
    setFilter: filterAdapter.setFilter,
    clearFilter: filterAdapter.clearFilter,
    clearAllFilters: filterAdapter.clearAllFilters,
    applyFilters: filterAdapter.applyFilters,
    setSort: sortingAdapter.setSort,
    setSorts: sortingAdapter.setSorts,
    resetSort: sortingAdapter.resetSort,
  },
  extraReducers: builder => {
    builder.addCase(fetchPayment.pending, (state, { meta: { arg } }) => {
      upsertOnePruneCache(state, { id: arg.id, isFetching: true, error: null });
    });
    builder.addCase(fetchPayment.fulfilled, (state, { meta: { arg }, payload: payment }) => {
      entityAdapter.upsertOne(state, { id: arg.id, entity: payment, isFetching: false, error: null });
    });
    builder.addCase(fetchPayment.rejected, (state, { meta: { arg }, error }) => {
      entityAdapter.upsertOne(state, { id: arg.id, entity: undefined, isFetching: false, error });
    });

    builder.addCase(fetchPaymentsPage.pending, (state, _) => {
      patch(state.listView, { isFetching: true, error: null });
    });
    builder.addCase(fetchPaymentsPage.fulfilled, (state, { payload: { contents, totalCount } }) => {
      const payments = normalize(contents);
      patch(state.listView, { ids: map(payments, 'id'), totalCount, isFetching: false });
      entityAdapter.upsertMany(state, payments);
      pruneCache(state);
    });
    builder.addCase(fetchPaymentsPage.rejected, (state, { error }) => {
      patch(state.listView, { ids: [], totalCount: null, isFetching: false, error });
      pruneCache(state);
    });
  },
});

const normalize = (payments: PaymentPageItem[]): PaymentEntity[] =>
  map(payments, payment => ({ id: payment.id, entity: payment, isFetching: false, error: null }));

export const paymentsSelectors = entityAdapter.getSelectors<MerchantAdminState>(state => state.paymentsPage);

export const {
  actions: {
    resetError,
    resetEntityError,
    setPage,
    setPageSize,
    setFilter,
    clearFilter,
    clearAllFilters,
    applyFilters,
    setSorts,
    resetSort,
    setInited,
    setFilterOverride,
    clearFilterOverride,
  },
  reducer: paymentsReducer,
} = paymentsPageSlice;

export { emptyFilters as emptyPaymentFilters };
